import {maxBy, minBy} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural, Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {Loader, LoaderVariantsEnum} from '../../../../../../libs/shared/ui/src/components/loaders/loader';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {rotate360Animation} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  buildStandardAvailabilitySummary,
  TrueAvailabilityStatusesEnum,
} from '../../../../../helpers/models/schedulingLinkAvailabilityHelpers';
import {isSchedulingLinkAvailable} from '../../../../../helpers/models/schedulingLinkHelpers';
import {
  DraftSchedulingLinkModel,
  SchedulingLinkStandardAvailabilityBlockModel,
} from '../../../../../models/api/schedulingLinkModel';

/*
 * Props.
 */

interface MeetingSettingsPanelFooterProps {
  selectedSlots: ReadonlyArray<SchedulingLinkStandardAvailabilityBlockModel>;
  draftLink: DraftSchedulingLinkModel;
  onSaveClick: () => void;
  onClearClick: () => void;
  trueAvailabilityStatus: TrueAvailabilityStatusesEnum;
  calendarNames: ReadonlyArray<string>;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  padding: 15px 12px;
  display: grid;
  grid-template-areas:
    'slots slots'
    'loading loading'
    'clear save';
`;

const StyledSummaryDiv = styled.div`
  grid-area: slots;
  display: grid;
  grid-template-areas: 'icon text';
  grid-template-columns: 14px 1fr;
  grid-column-gap: 4px;
  margin-bottom: 12px;
`;

const StyledSummaryIconDiv = styled.div`
  grid-area: icon;
  margin-top: 3px;
`;

const StyledSummaryTextDiv = styled.div`
  grid-area: text;
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 20px;
  word-break: break-word;
`;

const StyledSpinnerWrapperDiv = styled.div`
  grid-area: loading;
  margin: 5px;
`;

const StyledSpinnerDiv = styled.div`
  height: 20px;
  width: 20px;
  ${rotate360Animation(2)};
  margin: auto;
`;

const StyledSecondaryButtonDiv = styled(Button)`
  grid-area: clear;
  justify-self: start;
`;

const StyledPrimaryButtonDiv = styled(Button)`
  grid-area: save;
  justify-self: end;
`;

const StyledCalendarsCountDiv = styled.div`
  display: inline-block;
  text-decoration: underline;
`;

/*
 * Component.
 */

export const MeetingSettingsPanelFooter: FC<MeetingSettingsPanelFooterProps> = (props) => (
  <StyledWrapperDiv>
    {maybeRenderSummary(props)}
    {maybeRenderActions(props)}
  </StyledWrapperDiv>
);

/*
 * Helpers.
 */

function hasAvailability(props: MeetingSettingsPanelFooterProps) {
  // If the server says we don't have availability, we don't have availability.
  if (props.trueAvailabilityStatus === TrueAvailabilityStatusesEnum.NO_AVAILABILITY) {
    return false;
  }

  // Do a primitive check for availability client-side.
  return isSchedulingLinkAvailable(props.draftLink);
}

function maybeRenderSummary(props: MeetingSettingsPanelFooterProps) {
  const firstSlot = minBy(props.selectedSlots, (slot) => slot.start);
  const lastSlot = maxBy(props.selectedSlots, (slot) => slot.end);

  if (!firstSlot || !lastSlot || !props.selectedSlots) {
    return null;
  }

  return (
    <StyledSummaryDiv>
      <StyledSummaryIconDiv>{renderSummaryIcon(props)}</StyledSummaryIconDiv>
      <StyledSummaryTextDiv>{renderSummaryText(props)}</StyledSummaryTextDiv>
    </StyledSummaryDiv>
  );
}

function renderSummaryIcon(props: MeetingSettingsPanelFooterProps) {
  if (hasAvailability(props)) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name="checkmarkYesActivity" color={theme.palette.green.shade40} />}
      </UserThemeConsumer>
    );
  }

  return (
    <UserThemeConsumer>
      {(theme) => <Icon name="warningActivity" color={theme.palette.red.shade40} />}
    </UserThemeConsumer>
  );
}

function maybeRenderActions(props: MeetingSettingsPanelFooterProps) {
  // Don't display the actions if we're loading.
  if (props.trueAvailabilityStatus === TrueAvailabilityStatusesEnum.LOADING) {
    return (
      <StyledSpinnerWrapperDiv>
        <StyledSpinnerDiv>
          <Loader size={VisualSizesEnum.MEDIUM} variant={LoaderVariantsEnum.BASE} isEnabled />
        </StyledSpinnerDiv>
      </StyledSpinnerWrapperDiv>
    );
  }

  return (
    <>
      {props.selectedSlots.length > 0 && (
        <StyledSecondaryButtonDiv variant={ButtonVariantsEnum.LINK} onClick={props.onClearClick}>
          <Trans id="scheduling-meetingtimes-clearall">Clear all</Trans>
        </StyledSecondaryButtonDiv>
      )}
      <StyledPrimaryButtonDiv
        variant={ButtonVariantsEnum.PRIMARY}
        isDisabled={!hasAvailability(props)}
        onClick={props.onSaveClick}
      >
        <Trans id="scheduling-meetingtimes-addtoemail">Add options to email</Trans>
      </StyledPrimaryButtonDiv>
    </>
  );
}

function renderSummaryText(props: MeetingSettingsPanelFooterProps) {
  const slotSummary = buildStandardAvailabilitySummary(props.selectedSlots);
  const numCalendars = props.calendarNames.length;
  const calendarsSummary = props.calendarNames.join(', ');

  return (
    <Plural
      id="scheduling-meetingtimes-calendars"
      value={numCalendars}
      one={
        <>
          {slotSummary} on the {calendarsSummary} calendar
        </>
      }
      other={
        <>
          {slotSummary} on{' '}
          <StyledCalendarsCountDiv>
            <TooltipCoordinator render={() => calendarsSummary}>{numCalendars} calendars</TooltipCoordinator>
          </StyledCalendarsCountDiv>
        </>
      }
    />
  );
}
